import {
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  Checkbox,
  Button,
  Input,
  Avatar,
  Badge,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Textarea,
  Tooltip,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import IconBox from "../../../../components/icons/IconBox";
import { MdCheckBox, MdAdd, MdDelete } from "react-icons/md";
import React, { useState, useEffect } from "react";
import { employeeService } from '../../../../services/employeeService';
import { taskService } from '../../../../services/taskService';
import dayjs from 'dayjs';
import { authService } from '../../../../services/authService';

export default function Tasks(props) {
  // Renk değişkenleri
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "navy.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const brandColor = useColorModeValue("brand.500", "brand.400");

  // State tanımlamaları
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    dueDate: "",
    assignee: "",
    priority: "Orta"
  });
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 2;

  // Görevleri getir
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const tasksData = await taskService.getTasks();
      setTasks(tasksData || []);
    } catch (error) {
      console.error("Görevler getirilirken hata:", error);
      toast({
        title: "Hata",
        description: "Görevler yüklenirken bir sorun oluştu",
        status: "error",
        duration: 3000,
      });
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Çalışanları getir
  const fetchEmployees = async () => {
    try {
      const employeesData = await employeeService.getEmployeesForTasks();
      setEmployees(employeesData || []);
    } catch (error) {
      console.error("Çalışanlar getirilirken hata:", error);
      setEmployees([]);
    }
  };

  // Component yüklendiğinde çalışanları ve görevleri getir
  useEffect(() => {
    fetchTasks();
    fetchEmployees();
  }, []);

  // Görev ekleme
  const handleAddTask = async () => {
    try {
      // Validasyon
      if (!newTask.title || !newTask.dueDate || !newTask.assignee) {
        toast({
          title: "Hata",
          description: "Başlık, bitiş tarihi ve görevli alanları zorunludur",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // API'ye gönderilecek veriyi hazırla
      const taskData = {
        ...newTask,
        restaurantId: authService.getCurrentRestaurantId()
      };

      // API çağrısı
      const response = await taskService.createTask(taskData);
      
      if (response) {
        await fetchTasks();
        setIsModalOpen(false);
        setNewTask({
          title: "",
          description: "",
          dueDate: "",
          assignee: "",
          priority: "Orta"
        });
        
        toast({
          title: "Başarılı",
          description: "Görev eklendi",
          status: "success",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Hata:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Görev eklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Görev silme
  const handleDeleteTask = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      fetchTasks();
      toast({
        title: "Başarılı",
        description: "Görev silindi",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Görev silinirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Görev durumu güncelleme
  const handleTaskComplete = async (taskId, completed) => {
    try {
      await taskService.updateTask(taskId, { completed: !completed });
      fetchTasks();
      toast({
        title: "Başarılı",
        description: "Görev durumu güncellendi",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Görev güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Pagination hesaplamaları
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = tasks.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(tasks.length / tasksPerPage);

  return (
    <Card p='20px' align='center' direction='column' w='100%' {...props}>
      <Flex alignItems='center' w='100%' mb='30px'>
        <IconBox
          me='12px'
          w='38px'
          h='38px'
          bg={boxBg}
          icon={<Icon as={MdCheckBox} color={brandColor} w='24px' h='24px' />}
        />
        <Text color={textColor} fontSize='lg' fontWeight='700'>
          Görevler
        </Text>
        <Button
          ms='auto'
          leftIcon={<MdAdd />}
          colorScheme='brand'
          onClick={() => setIsModalOpen(true)}
        >
          Yeni Görev
        </Button>
      </Flex>

      <Box flex="1" overflowY="auto" w="100%" mb="4">
        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" color="brand.500" />
          </Flex>
        ) : currentTasks.length > 0 ? (
          currentTasks.map((task) => (
            <Flex
              key={task._id}
              mb='3'
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              direction={{ base: 'column', md: 'row' }}
              align="center"
              p="3"
              gap={{ base: 2, md: 3 }}
              borderWidth="1px"
              borderColor={task.completed ? "green.200" : "gray.200"}
              _hover={{
                borderColor: task.completed ? "green.300" : brandColor
              }}
            >
              {/* Checkbox */}
              <Checkbox
                colorScheme='brandScheme'
                isChecked={task.completed}
                onChange={() => handleTaskComplete(task._id, task.completed)}
                size="md"
              />

              {/* Atanan Kişi */}
              <Tooltip
                label={task.assignee ? `${task.assignee.name} - ${task.assignee.position}` : "Atanmamış"}
                placement="top"
                hasArrow
                openDelay={400}
                bg="gray.200"
                color="black"
              >
                <Avatar 
                  size="sm" 
                  name={task.assignee ? task.assignee.name : "?"} 
                  cursor="pointer"
                />
              </Tooltip>

              {/* Başlık ve Açıklama */}
              <Box flex="1">
                <Text
                  fontWeight='bold'
                  color={textColor}
                  fontSize='sm'
                  textDecoration={task.completed ? 'line-through' : 'none'}
                  opacity={task.completed ? 0.7 : 1}
                >
                  {task.title}
                </Text>
                {task.description && (
                  <Tooltip 
                    label={task.description}
                    placement="top"
                    hasArrow
                    openDelay={400}
                    bg="gray.200"
                    color="black"
                  >
                    <Text 
                      fontSize="xs" 
                      color="gray.600"
                      mt="0.5"
                      noOfLines={2}
                      cursor="pointer"
                      _hover={{ color: "gray.800" }}
                    >
                      {task.description}
                    </Text>
                  </Tooltip>
                )}
              </Box>

              {/* Sağ Kısım - Detaylar ve Aksiyonlar */}
              <Flex 
                flex="1"
                direction="column"
                justify="space-between"
                align="flex-end"
              >
                <Flex gap="2" mb="1">
                  <Badge 
                    colorScheme={
                      task.priority === 'Yüksek' ? 'red' : 
                      task.priority === 'Orta' ? 'yellow' : 'green'
                    }
                    variant="subtle"
                    fontSize="xs"
                  >
                    {task.priority}
                  </Badge>
                  <Badge 
                    colorScheme={task.completed ? 'green' : 'gray'}
                    fontSize="xs"
                  >
                    {task.completed ? 'Tamamlandı' : 'Bekliyor'}
                  </Badge>
                </Flex>

                <Flex align="center" gap="2">
                  <Text fontSize="xs" color="gray.500">
                    {task.dueDate ? dayjs(task.dueDate).format('DD.MM.YYYY') : "Tarih yok"}
                  </Text>
                  <IconButton
                    icon={<MdDelete />}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDeleteTask(task._id)}
                    size="md"
                  />
                </Flex>
              </Flex>
            </Flex>
          ))
        ) : (
          <Flex justify="center" align="center" h="200px" direction="column" gap={3}>
            <Icon as={MdCheckBox} w={12} h={12} color="gray.300" />
            <Text color="gray.500">Henüz görev bulunmuyor</Text>
          </Flex>
        )}
      </Box>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>Yeni Görev</ModalHeader>
          <ModalBody>
            <FormControl mb="4" isRequired>
              <FormLabel>Başlık</FormLabel>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                placeholder="Görev başlığı"
              />
            </FormControl>

            <FormControl mb="4">
              <FormLabel>Açıklama</FormLabel>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Görev açıklaması"
              />
            </FormControl>

            <FormControl mb="4" isRequired>
              <FormLabel>Görevli</FormLabel>
              <Select
                value={newTask.assignee}
                onChange={(e) => setNewTask({...newTask, assignee: e.target.value})}
                placeholder="Görevli seçin"
              >
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.name} - {employee.position}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl mb="4">
              <FormLabel>Öncelik</FormLabel>
              <Select
                value={newTask.priority}
                onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
              >
                <option value="Düşük">Düşük</option>
                <option value="Orta">Orta</option>
                <option value="Yüksek">Yüksek</option>
              </Select>
            </FormControl>

            <FormControl mb="4" isRequired>
              <FormLabel>Bitiş Tarihi</FormLabel>
              <Input
                type="datetime-local"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              İptal
            </Button>
            <Button colorScheme="brand" onClick={handleAddTask}>
              Ekle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Pagination */}
      <Flex justify="center" mt="4" gap="2">
        <Button
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          isDisabled={currentPage === 1}
        >
          Önceki
        </Button>
        <Text alignSelf="center">
          Sayfa {currentPage} / {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          isDisabled={currentPage === totalPages}
        >
          Sonraki
        </Button>
      </Flex>
    </Card>
  );
}